import * as React from "react";
import moment from "moment";
import groupby from "lodash.groupby";
import { IChartDataItemV2 } from "@iliotech/types";
import { ClickableLegendItem } from "@iliotech/component-library";
import {
  addAlpha,
  convertDataToQuarter,
  convertDataToYears,
  getBackgroundColor,
  reduceArrays,
} from "@iliotech/data-wire/src/utils";
import {
  IGlobalPeriodContext,
  IStructureGrouping,
  useGlobalCurrency,
  useGlobalPeriod,
  useStructureGrouping,
} from "@iliotech/data-wire";
import { DRILL_DOWNS } from "@iliotech/data-wire/src/utils";
import { useSmallScreen } from "@iliotech/data-wire/src/reactHooks/hooks/useSmallScreen";

interface IIncomeItem {
  date: moment.Moment;
  instrumentName: string;
  amountBase: number;
  amountLocal: number;
  currencyLocalSymbol: string;
}

interface UseIncomeByAssetClassWidgetProps {
  pastIncome: IIncomeItem[];
  periodContext?: IGlobalPeriodContext;
  structureContext?: IStructureGrouping;
}

export const useIncomeByAssetClassWidget = ({
  pastIncome,
  periodContext,
  structureContext,
}: UseIncomeByAssetClassWidgetProps) => {
  const { period } = periodContext || useGlobalPeriod();
  const { currencyFormatter } = useGlobalCurrency();
  const { selectedGroup, setSelectedGroup, grouping } =
    structureContext || useStructureGrouping();
  const isMobile = useSmallScreen();

  const filterDataByPeriod = (data: IChartDataItemV2[]) => {
    return (data || []).filter((item: IChartDataItemV2) => {
      return (
        moment(item.date).isSameOrAfter(period?.from, "days") &&
        moment(item.date).isSameOrBefore(period?.to, "days")
      );
    });
  };

  const pastDataTotalAbsolute = React.useMemo(() => {
    return (pastIncome || [])
      .filter((item) =>
        moment(item.date)?.isAfter(moment().subtract(1, "year").toDate())
      )
      .map((item) => item.amountBase)
      .reduce((a, b) => a + b, 0);
  }, [pastIncome]);

  const { pastIncomeTotals, pastIncomeData } = React.useMemo(() => {
    const filterField = grouping.field;
    const filterLabel = grouping.labelField;
    const filterId = selectedGroup?.code;
    const aggregateField = (
      !filterId
        ? filterField
        : DRILL_DOWNS[grouping.field]?.field || filterField
    ) as keyof IIncomeItem;

    const aggregateLabel = (
      !filterId
        ? filterLabel
        : DRILL_DOWNS[grouping.field]?.labelField || filterLabel
    ) as keyof IIncomeItem;

    const getChartItemFromIncomePoint = (item: any): IChartDataItemV2 => {
      const getColor = () => {
        const color =
          grouping?.getColor?.(item[aggregateField] as string) ||
          getBackgroundColor((item[aggregateField] as any) || "null");
        if (filterId) {
          return item.isLong ? color : addAlpha(color, 0.6);
        }
        return color;
      };

      return {
        category: item[aggregateLabel as keyof IIncomeItem] as any,
        name: moment(item.date).format("MMM 'YY"),
        date: moment(item.date).toDate() as any,
        id: item.instrumentName,
        color: getColor(),
        value: item.amountBase,
        tooltipExtra: item.amountBase
          ? [
              {
                label: item.instrumentName,
                value:
                  item.amountLocal === item.amountBase
                    ? currencyFormatter!(item.amountBase)
                    : `${item.currencyLocalSymbol} ${
                        item.amountLocal
                      } / ${currencyFormatter!(item.amountBase)}`,
              },
            ]
          : [],
      } as any;
    };

    const processIncomeData = (data: any[]) => {
      const groupedData: { [grouping: string]: IChartDataItemV2[] } = {};
      const filteredData = filterId
        ? (data || []).filter((s: any) => s[filterField] === filterId)
        : data;

      filteredData?.forEach((item) => {
        groupedData[item[aggregateField] as string] =
          groupedData[item[aggregateField] as string] || [];

        groupedData[item[aggregateField] as string].push(
          getChartItemFromIncomePoint(item as any)
        );
      });

      let incomeData = reduceArrays(Object.values(groupedData));
      incomeData = filterDataByPeriod(incomeData);

      const itemsInCategoryAxis = Object.values(
        groupby(incomeData, (item: IChartDataItemV2) => item?.name)
      ).length;

      if (itemsInCategoryAxis > 20) {
        incomeData = isMobile
          ? convertDataToYears(incomeData)
          : convertDataToQuarter(incomeData);
      } else if (itemsInCategoryAxis > 15 && isMobile) {
        incomeData = convertDataToQuarter(incomeData);
      }

      let totals: ClickableLegendItem[] = [];
      const rawValue = [...incomeData]
        .map((item) => item.value)
        .reduce((a, b) => a + b, 0);

      totals.push({
        label: "Total",
        onClick: () => onReset(),
        rawValue,
        value: currencyFormatter!(rawValue),
      });

      Object.keys(groupedData).forEach((key) => {
        const dataForLegend = filterDataByPeriod(groupedData[key]);
        const totalValue = dataForLegend
          .map((item) => item.value)
          .reduce((a, b) => a + b, 0);

        totals.push({
          label: dataForLegend[0]?.category,
          onClick: () => onItemClick(key, dataForLegend[0]?.category),
          value: currencyFormatter!(totalValue),
          rawValue: totalValue,
          color: groupedData[key][0].color,
        });
      });

      return {
        data: incomeData,
        totals: totals.filter(
          (item) => item.rawValue! >= 1 || item.rawValue! <= -1
        ),
      };
    };

    const onItemClick = (code: string, label: string) => {
      if (!selectedGroup?.code) {
        setSelectedGroup?.({ code, label });
      } else {
        setSelectedGroup?.(undefined);
      }
    };

    const onReset = () => {
      setSelectedGroup?.(undefined);
    };

    const processedData = processIncomeData(pastIncome);

    return {
      pastIncomeTotals: processedData.totals,
      pastIncomeData: processedData.data,
    };
  }, [
    pastIncome,
    selectedGroup,
    grouping,
    currencyFormatter,
    period,
    filterDataByPeriod,
    setSelectedGroup,
    isMobile,
  ]);

  const pastIncomeTitle = React.useMemo(
    () =>
      !pastIncomeData?.length
        ? `No income by ${
            !selectedGroup?.code ? grouping.label : selectedGroup.label
          }`
        : `Income by ${
            !selectedGroup?.code ? grouping.label : selectedGroup?.label
          }`,
    [selectedGroup, pastIncomeData, grouping.label]
  );

  return {
    pastDataTotalAbsolute,
    pastIncomeTotals,
    pastIncomeData,
    pastIncomeTitle,
    selectedGroup,
  };
};
